import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { InputAdornment } from '@mui/material'

import axios from '../../dataClient/dataClient'
import { formatDateInput } from '../../util/strings'
import { StyledDialog, FlexEndContainer, FlexDialogContent, FlexColumn } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle } from '../styles/Text'
import CalendarDateButton from '../common/CalendarDateButton'
import FormattedInput from '../inputFields/FormattedInput'

const FlexDateColumn = styled(FlexColumn)`
  margin-top: -30px;
`

const acceptFoiFormSchema = yup.object().shape({
  startDate: yup
    .string()
    .required('Start date is required')
    .test('valid-date', 'Please enter a valid date', value => {
      if (!value) return false
      try {
        const date = new Date(value)
        return !isNaN(date.getTime())
      } catch {
        return false
      }
    })
})

function AcceptFoiButton({ foiId, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [currentDate, setCurrentDate] = useState()

  const methods = useForm({
    resolver: yupResolver(acceptFoiFormSchema),
    defaultValues: {
      startDate: format(new Date(), 'MMM-dd-yyyy')
    }
  })
  const { handleSubmit, reset, setValue, watch } = methods

  const startDate = watch('startDate')

  useEffect(() => {
    if (startDate) {
      setCurrentDate(format(new Date(startDate), 'MMM-dd-yyyy'))
    }
  }, [startDate])

  const handleOpen = () => {
    setIsVisible(true)
    setError(false)
    reset({
      startDate: format(new Date(), 'MMM-dd-yyyy')
    })
    setCurrentDate(format(new Date(), 'MMM-dd-yyyy'))
  }

  const handleClose = () => {
    setIsVisible(false)
    setError(false)
    setIsLoading(false)
    reset({
      startDate: format(new Date(), 'MMM-dd-yyyy')
    })
  }

  const handleConfirmDate = date => setValue('startDate', format(date, 'MMM-dd-yyyy'))
  const onError = (errors, e) => console.error('Error', errors, e)

  function handleAcceptSubmit(data) {
    setIsLoading(true)
    setError(false)

    const startDateObj = new Date(data.startDate)

    axios
      .post(`/foi/${foiId}/accept`, { startDate: startDateObj }, { headers: { 'Content-Type': 'application/json' } })
      .then(() => {
        setIsLoading(false)
        handleClose()
        setTimeout(() => {
          onSuccess()
        }, 100)
      })
      .catch(e => {
        console.error('Failed accepting FOI', e)
        setError(true)
        setIsLoading(false)
      })
  }

  return (
    <>
      <Button aria-label="Accept FOI" onClick={handleOpen}>
        Accept FOI
      </Button>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false}>
        <FlexDialogContent>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleAcceptSubmit, onError)}>
              <FlexColumn gap="20px" width="100%">
                <BoldFormTitle>Accept FOI</BoldFormTitle>
                <BodyText>Accepting the FOI will create a request due date with the following start date:</BodyText>
                <FlexDateColumn>
                  {currentDate !== undefined && (
                    <FormattedInput
                      id="startDate"
                      label=""
                      handleFormatValue={formatDateInput}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarDateButton
                              initialValue={new Date(currentDate)}
                              title="Start Date"
                              onConfirm={handleConfirmDate}
                              disableFuture={false}
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                </FlexDateColumn>
                {error && <ErrorMessage>There was an error accepting the FOI. Please try again.</ErrorMessage>}
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose} disabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                  <Button type="submit" loading={isLoading} disabled={isLoading}>
                    Accept
                  </Button>
                </FlexEndContainer>
              </FlexColumn>
            </form>
          </FormProvider>
        </FlexDialogContent>
      </StyledDialog>
    </>
  )
}

AcceptFoiButton.propTypes = {
  foiId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func
}

export default AcceptFoiButton
